import { googleReferred } from '../../utils/adAttribution';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { discountLabel } from '../../utils/introOffer';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import { logger } from '../../utils/logger';
import {
  HandTitle,
  FunnelCharacterStage,
  sheetVariants,
  Chevron,
  CheckIcon,
} from './FunnelScreens';
import {
  BillingToggle,
  PlanGrid,
  PaywallFootnote,
  PaywallKeyframes,
  ReviewCard,
  priceable,
} from './FunnelPlans';
import { FunnelPriceChooser } from './FunnelPriceChooser';

/**
 * The last three screens of the in-house checkout funnel
 * (/funnel-auth), where RevenueCat's hosted paywall used to
 * be: the paywall, the card form, and the welcome after paying.
 *
 * The paywall is the funnel's own copy of the website's plans screen
 * (./FunnelPlans.js: the Monthly / Annual switch, the Pro + Family cards, the
 * fine print, the review), tuned for the funnel and independent of the site's
 * TrialWall. The card
 * form and the welcome keep the funnel's night-sky look, with the character
 * the parent just made.
 *
 * The card form is Stripe Embedded Checkout: Stripe's own iframe, mounted
 * inside our sheet, so card details never pass through Pixley (PCI SAQ A).
 * Pixley only ever holds the session's client_secret, in React state for the
 * life of the step — never in storage, never in analytics, never in logs.
 *
 * Offer terms (plans, trial days, first-period discount) come from
 * GET /subscription-plans; Stripe coupon and price ids never reach the
 * browser. Prices shown here are display copies of what Stripe charges.
 */

// ── offer copy ─────────────────────────────────────────────────────────────

const money = (n) => `$${n.toFixed(2)}`;
// "$14.99/month" → 14.99; "$143.88/year" → 143.88
const parseMoney = (display) => {
  const m = String(display || '').match(/\$?\s*([0-9]+(?:\.[0-9]{1,2})?)/);
  return m ? Number(m[1]) : null;
};

export const FUNNEL_PLANS = {
  pro: {
    tier: 'pro',
    name: 'Pro',
    tagline: 'Best for most families',
    badge: 'MOST POPULAR',
    perks: ['Stories up to 3 minutes', 'Every language & format'],
  },
  family: {
    tier: 'family',
    name: 'Family',
    tagline: 'For two or more kids',
    badge: null,
    perks: ['Up to 5 child profiles', 'Downloads with no watermark'],
  },
};

// The iOS app, offered on the success screen with Apple's own badge
// (public/assets/app-store-badge-black.svg, Apple's artwork, used as-is)
// Apple's campaign link (App Store Connect provider token + a campaign
// name), so App Analytics counts the installs this screen sends — the same
// token as /download and the make site. A Google-referred visitor is named
// as such, which is the only install signal Google Ads traffic leaves.
export const APP_STORE_PROVIDER_TOKEN = '128151348';
export const appStoreUrl = (ct = 'web2app') =>
  `https://apps.apple.com/app/apple-store/id6751121342?pt=${APP_STORE_PROVIDER_TOKEN}&ct=${encodeURIComponent(ct)}&mt=8`;
export const APP_STORE_URL = appStoreUrl();
export const APP_STORE_BADGE = '/assets/app-store-badge-black.svg';

export const PRICING_UNAVAILABLE = "Sorry, pricing isn't available right now. Please try again later.";

// { [tier]: { monthly, annual } } in cents, from `plans[].price_cents` — what
// Stripe says the prices are. Nothing on the paywall is priced any other way.
export const pricesFrom = (plansData) => {
  const out = {};
  for (const plan of plansData?.plans || []) {
    const cents = plan?.price_cents;
    if (plan?.id && cents) out[plan.id] = { monthly: cents.monthly, annual: cents.annual };
  }
  return out;
};

// ── the paywall offer experiment ───────────────────────────────────────────
// Three arms on the Pro card (hooks/useOfferVariant: control $7.99 / lower
// $4.99 / pwyw "pay what you want"), each priced by the backend and served in
// `funnel_offer_variants` on /subscription-plans, keyed by arm. An arm the
// backend reports unconfigured (a coupon id not set yet), or a plans response
// from before the experiment, is control: the page shows control's price and
// names control on checkout, so what is reported is what was charged.

export const servedOfferVariant = (plansData, variant) => {
  const arm = plansData?.funnel_offer_variants?.[variant];
  return arm && arm.configured !== false ? variant : 'control';
};

/** The offer block (same shape as `funnel_intro_offer`) for the arm that is
 * actually served to this visitor. */
export const funnelOfferFor = (plansData, variant = 'control') => {
  const control = plansData?.funnel_intro_offer || null;
  const served = servedOfferVariant(plansData, variant);
  // Control is `funnel_intro_offer` itself; and when Stripe could not be read
  // (control unavailable) every arm is, since they read the same prices
  if (served === 'control' || control?.available === false) return control;
  return plansData.funnel_offer_variants[served];
};

// Pay what you want: the price the parent named replaces Pro's suggested
// first month, said with its own discount label ("65%+" for $4.99), computed
// the way the backend computes it so the chip can never disagree.
const withChosenCents = (funnelOffer, chosenCents) => {
  const pro = funnelOffer?.plans?.pro;
  const regular = pro?.regular_cents?.monthly;
  if (!Number.isInteger(chosenCents) || !pro || !Number.isFinite(regular)) return funnelOffer;
  return {
    ...funnelOffer,
    plans: {
      ...funnelOffer.plans,
      pro: {
        ...pro,
        first_period_cents: { ...pro.first_period_cents, monthly: chosenCents },
        discount_label: { ...pro.discount_label, monthly: discountLabel(regular, chosenCents) || null },
      },
    },
  };
};

/** The pwyw chooser's terms for the month ({ stops, min_cents, max_cents,
 * suggested_cents }), or null when this visitor doesn't get a chooser (another
 * arm, annual billing, or no such block from the server). */
export const pwywChoiceFor = (plansData, variant, billing) => (
  servedOfferVariant(plansData, variant) === 'pwyw' && billing === 'monthly'
    ? funnelOfferFor(plansData, variant)?.plans?.pro?.choose?.monthly || null
    : null
);

/** The first-month price the parent named, if it is one the server offers
 * right now; null otherwise. A price kept from an earlier visit whose coupon
 * has since gone, or one from another arm, is treated as no choice: the card
 * shows the suggested price rather than a number checkout would refuse. */
export const validChosenCents = (plansData, variant, chosenCents) => {
  if (!Number.isInteger(chosenCents)) return null;
  const choose = pwywChoiceFor(plansData, variant, 'monthly');
  if (!choose) return null;
  if (Array.isArray(choose.stops)) return choose.stops.includes(chosenCents) ? chosenCents : null;
  const { min_cents: min, max_cents: max } = choose;
  return Number.isFinite(min) && Number.isFinite(max) && chosenCents >= min && chosenCents <= max ? chosenCents : null;
};

/** The funnel offer this visitor is priced from: their arm's terms, with the
 * price they named (when the server offers it) laid over the first month in
 * the pwyw arm. `experiment` = { variant, chosenCents }. */
export const resolveFunnelOffer = (plansData, { variant, chosenCents } = {}) => {
  const base = funnelOfferFor(plansData, variant);
  return servedOfferVariant(plansData, variant) === 'pwyw'
    ? withChosenCents(base, validChosenCents(plansData, variant, chosenCents))
    : base;
};

/**
 * What a plan costs this visitor, in words: the first charge, what it renews
 * at, and whether it starts with a trial. `plansData` is the body of
 * GET /subscription-plans (`data`); any missing piece falls back to the
 * list price so the screen never shows a blank.
 */
export const describeOffer = (plansData, tier = 'pro', billing = 'monthly', promo = null, experiment = {}) => {
  const def = FUNNEL_PLANS[tier] || FUNNEL_PLANS.pro;
  const annual = billing === 'annual';
  const introPlan = plansData?.intro_offer?.plans?.[tier];
  const prices = pricesFrom(plansData);
  const perShort = annual ? 'yr' : 'mo';
  const offerVariant = servedOfferVariant(plansData, experiment?.variant);
  const chosenCents = !annual && tier === 'pro' ? validChosenCents(plansData, experiment?.variant, experiment?.chosenCents) : null;

  // Nothing is priced until Stripe's amounts are in hand: no fallbacks
  const funnelOffer = resolveFunnelOffer(plansData, experiment);
  const offerBlocked = funnelOffer?.enabled && funnelOffer?.plans?.[tier] && funnelOffer.plans[tier].available === false;
  if (!priceable(prices, tier) || offerBlocked) {
    return {
      ...def, billing, kind: 'unavailable', available: false,
      regular: null, regularDisplay: '', monthlyEquivalent: '', discounted: null, trialDays: 0,
      headline: '', headlineNote: '', strike: null, chip: null, firstLine: PRICING_UNAVAILABLE,
      renewal: '', saves: 0, chargeValue: 0, offerVariant, chosenCents,
    };
  }
  const monthly = prices[tier].monthly / 100;
  const annualTotal = prices[tier].annual / 100;

  const regular = annual ? annualTotal : monthly;
  const per = annual ? 'year' : 'month';
  const promoCents = tier === 'pro' ? promo?.firstPeriodCents?.[billing] : undefined;
  // A promotion code's price first, then the arm's exact first-period price
  // for this period (Stripe's coupon on Stripe's price, or the price the
  // parent named), else the website's percentage
  const fixedCents = promoCents ?? funnelOffer?.plans?.[tier]?.first_period_cents?.[billing];
  // Naming the regular price in the pwyw arm is no discount at all: a plain
  // first month, not an "intro offer" of 0%
  const atRegular = !Number.isFinite(promoCents) && Number.isInteger(chosenCents) && chosenCents >= Math.round(regular * 100);
  const introEnabled = (!!(plansData?.intro_offer?.enabled && introPlan?.enabled) || Number.isFinite(promoCents)) && !atRegular;
  const trialDays = introEnabled ? 0 : Number(plansData?.trial?.days || 0);
  const trialPrice = plansData?.trial?.intro_price_display || '';

  let kind = 'plain';
  let headline = money(regular);
  let headlineNote = `per ${per}`;
  let firstLine = `${money(regular)}/${per}. Cancel anytime.`;
  let strike = null;
  let chip = null;
  let discounted = null;

  if (introEnabled) {
    kind = 'intro';
    const pct = Number(introPlan?.discount_percent || 45);
    discounted = Number.isFinite(fixedCents) && fixedCents >= 0 && fixedCents < regular * 100
      ? fixedCents / 100
      : Math.round(regular * 100 * ((100 - pct) / 100)) / 100;
    headline = money(discounted);
    headlineNote = annual ? 'for your first year' : 'for your first month';
    strike = money(regular);
    // Said the way the coupon / code itself is said, else from the two prices
    const said = Number.isFinite(promoCents)
      ? String(promo?.discountDisplay || '').replace(/\s*off$/i, '')
      : funnelOffer?.plans?.[tier]?.discount_label?.[billing];
    chip = `${said || discountLabel(Math.round(regular * 100), Math.round(discounted * 100))} off`;
    firstLine = `${money(discounted)} today for your first ${annual ? 'year' : 'month'}, then ${money(regular)}/${per}. Cancel anytime.`;
  } else if (trialDays > 0) {
    kind = 'trial';
    headline = trialPrice ? trialPrice : 'Free';
    headlineNote = `for ${trialDays} days`;
    chip = `${trialDays}-day trial`;
    firstLine = trialPrice
      ? `${trialPrice} today for ${trialDays} days, then ${money(regular)}/${per}. Cancel anytime.`
      : `Free for ${trialDays} days, then ${money(regular)}/${per}. Cancel anytime before and you won't be charged.`;
  }

  // Yearly saves against twelve monthly payments (floored to a five)
  const saves = annual && monthly * 12 > annualTotal
    ? parseInt(discountLabel(Math.round(monthly * 1200), Math.round(annualTotal * 100)) || '0', 10)
    : 0;

  return {
    ...def,
    billing,
    kind,
    available: true,
    regular,
    regularDisplay: `${money(regular)}/${perShort}`,
    monthlyEquivalent: annual ? money(annualTotal / 12) : money(monthly),
    discounted,
    trialDays,
    headline,
    headlineNote,
    strike,
    chip,
    firstLine,
    renewal: `then ${money(regular)}/${perShort}`,
    saves,
    // The browser pixels' purchase value: what is charged at checkout
    chargeValue: kind === 'intro' ? discounted : kind === 'trial' ? (parseMoney(trialPrice) ?? 0) : regular,
    // The experiment arm this was priced in, and the first month the parent
    // named (pwyw, monthly) — reported on every checkout event
    offerVariant,
    chosenCents,
  };
};

const possessive = (name) => (/s$/i.test(name) ? `${name}'` : `${name}'s`);

// ── icons (inline, like FunnelScreens — no icon-font wait) ─────────────────


const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
    <rect x="5" y="10.5" width="14" height="10" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="15.5" r="1.4" fill="currentColor" />
  </svg>
);

// ── paywall ────────────────────────────────────────────────────────────────
// The sign-in step's stage, then the website's paywall. The character the
// parent just made stands on the moonlit stage exactly as it did a moment ago
// when they signed in, and the white sheet that rises under it carries the
// funnel's own paywall pieces (./FunnelPlans.js: the Monthly / Annual
// switch, the Pro + Family cards, the fine print and the review), a copy of
// the site's plans screen that belongs to the funnel alone.

/**
 * Offer terms for the website pieces, from GET /subscription-plans (`data`).
 * Until the request answers, the site's own defaults apply (first-period
 * discount on both plans), so the screen never renders empty.
 */
// The funnel's own first-period prices (`funnel_intro_offer` on
// /subscription-plans: exact cents per plan and period, from the backend's
// fixed Pro coupons) laid over the website's percentage terms.
const withFunnelOffer = (intro, funnelOffer) => {
  if (!funnelOffer?.plans) return intro;
  const plans = { ...(intro.plans || {}) };
  for (const [tier, terms] of Object.entries(funnelOffer.plans)) {
    if (!terms || terms.enabled === false || !plans[tier]) continue;
    // discount_label: how the discount is said per period ("45%+"), decided
    // server-side from the coupon itself
    plans[tier] = { ...plans[tier], first_period_cents: terms.first_period_cents, regular_cents: terms.regular_cents, discount_label: terms.discount_label };
  }
  return { ...intro, plans };
};

// A promotion code from the funnel URL (utils/funnelPromo) replaces Pro's
// first-period prices with the code's, on the regular price.
const withPromo = (introOffer, promo) => {
  if (!promo?.firstPeriodCents || !introOffer?.plans?.pro) return introOffer;
  const label = String(promo.discountDisplay || '').replace(/\s*off$/i, '');
  return { ...introOffer, plans: { ...introOffer.plans, pro: {
    ...introOffer.plans.pro, enabled: true, first_period_cents: promo.firstPeriodCents,
    discount_label: label ? { monthly: label, annual: label } : undefined,
  } } };
};

export const paywallTermsFrom = (plansData, promo = null, experiment = {}) => {
  const intro = plansData?.intro_offer || { enabled: false, plans: {} };
  const trialDays = Number(plansData?.trial?.days || 0);
  const hasIntroOffer = intro.enabled !== false;
  const funnelOffer = resolveFunnelOffer(plansData, experiment);
  const base = withFunnelOffer(intro, funnelOffer);
  const prices = pricesFrom(plansData);
  const available = priceable(prices, 'pro')
    && !(funnelOffer?.enabled && funnelOffer?.available === false);
  return {
    available,
    prices,
    hasIntroOffer: hasIntroOffer || !!promo,
    introOffer: promo ? withPromo({ ...base, enabled: true }, promo) : base,
    trialConfig: hasIntroOffer || trialDays <= 0
      ? null
      : { duration_days: trialDays, price_display: plansData?.trial?.intro_price_display || '' },
  };
};

// Confetti that falls from the top of the screen once, as the paywall opens:
// the same colours as the meet step's burst, over the whole viewport, gone
// after it has fallen. Never shown twice in a session. Rendered on <body>
// through a portal: the sheet it is triggered from is moved by framer-motion
// transforms, and a transformed ancestor would box the fixed-position layer
// into the sheet (off-centre, clipped) instead of the screen.
const CONFETTI_COLORS = ['#0A6CFF', '#FFC83D', '#7C5CFF', '#34C759', '#FF6FA5', '#00CAFF'];
const CONFETTI_MS = 4200;
export const PaywallEntryConfetti = ({ count = 56 }) => {
  const pieces = useRef(
    Array.from({ length: count }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.9,
      dur: 2.4 + Math.random() * 1.5,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      drift: (Math.random() - 0.5) * 180,
      spin: 360 + Math.random() * 540,
      size: 8 + Math.round(Math.random() * 5),
    }))
  ).current;
  return createPortal(
    <div className="fc-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: Math.round(p.size * 0.55),
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            '--drift': `${p.drift}px`,
            '--spin': `${p.spin}deg`,
          }}
        />
      ))}
    </div>,
    document.body
  );
};

// ── the stage and sheet both steps share ───────────────────────────────────
// The character and the white sheet stay put from the paywall into checkout
// and back; only what's on the sheet slides — left on the way to the card
// form, right on the way back to the plans — so paying feels like the next
// page of the same thing rather than a new screen.
const slideVariants = {
  enter: (dir) => ({ opacity: 0, x: dir >= 0 ? 48 : -48 }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1], staggerChildren: 0.05, delayChildren: 0.03 },
  },
  exit: (dir) => ({ opacity: 0, x: dir >= 0 ? -48 : 48, transition: { duration: 0.2, ease: 'easeIn' } }),
};

// The welcome, as a sequence (seconds after the payment lands):
//   0      the sheet drops off the bottom
//   0.35   the character leaves the stage: a flying copy of it (the same
//          cut-out) takes its exact place and drops toward the welcome
//   0.5    the welcome card's white fades in where it's going to land
//   1.15   it lands: the frame's border and the square still fade in over
//          the cut-out, the words stagger in
//   1.25   the ring fills; 1.45 the confetti; the star lands after the ring
const SHEET_DROP_S = 0.55;
export const CHARACTER_LEAVE_S = 0.35;
export const CHARACTER_MORPH_S = 0.8;
export const SUCCESS_CARD_DELAY_MS = 500;
export const SUCCESS_CARD_WORDS_MS = 1200;
export const SUCCESS_CONFETTI_DELAY_MS = 1450;
// A ready-made friend's cut-out, once it has flown in, IS the frame's picture:
// centred, as tall as the frame's inside (its 7px border excluded), no
// backdrop — a character made from a photo brings its own background and a
// cut-out brings none. Nothing is swapped at landing, so nothing shifts.
const FRAME_PAD = 7;
const STAGE_CHARACTER = '.fs-signin-friend, .fs-signin-frame, .fs-signin-mascot';

/**
 * The stage both the paywall and the card form stand on — and, once the
 * payment is in, the welcome. `step` 'paywall' | 'checkout' slide `children`
 * across the sheet; 'success' drops the sheet off the bottom of the screen
 * while the character grows into the welcome's frame (`success`), which rises
 * in behind it.
 */
// The welcome card rises in behind the dropping sheet; named variants so the
// card's children (itemVariants) stagger in after it
// The card itself never moves or fades (the character's frame is inside it
// and flies in from the stage, measured against the card's final place): its
// white and its shadow fade in around the character, its words stagger in.
const welcomeCardVariants = {
  enter: {
    backgroundColor: 'rgba(255,255,255,0)',
    boxShadow: '0 24px 60px rgba(14,24,84,0), 0 2px 8px rgba(14,24,84,0)',
  },
  center: {
    backgroundColor: 'rgba(255,255,255,1)',
    boxShadow: '0 24px 60px rgba(14,24,84,0.26), 0 2px 8px rgba(14,24,84,0.10)',
    transition: {
      delay: SUCCESS_CARD_DELAY_MS / 1000, duration: 0.5, ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.06, delayChildren: SUCCESS_CARD_WORDS_MS / 1000,
    },
  },
};

export const FunnelPayStage = ({ step, friend, previewImage, childName = '', children, success = null }) => {
  const name = childName.trim();
  // Forward into checkout, back to the plans
  const dir = step === 'checkout' ? 1 : -1;
  const paid = step === 'success';
  // Where the character stands, kept current while the stage is up, so the
  // welcome's frame can start its drop from exactly there
  const stageRef = useRef(null);
  const fromRectRef = useRef(null);
  useEffect(() => {
    if (paid || !stageRef.current) return undefined;
    const measure = () => {
      const el = stageRef.current?.querySelector(STAGE_CHARACTER);
      if (!el) return;
      const r = el.getBoundingClientRect();
      // page coordinates: the checkout may be scrolled when the payment lands
      fromRectRef.current = {
        left: r.left + window.scrollX, top: r.top + window.scrollY, width: r.width, height: r.height,
      };
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  });
  return (
    <div className={`fs-signin fc-paywall${paid ? ' is-paid' : ''}`} data-testid="funnel-pay-stage">
      <PaywallKeyframes />
      <AnimatePresence initial={false}>
        {!paid && (
          // The stage is sign-in's, a little shorter: on a phone the first
          // plan's price has to be in view before anyone scrolls. It fades as
          // the frame takes over its spot.
          <motion.div
            key="stage"
            ref={stageRef}
            className="fc-stage-wrap"
            // vanishes in the same instant its flying copy appears
            exit={{ opacity: 0, transition: { delay: CHARACTER_LEAVE_S, duration: 0.02 } }}
          >
            <FunnelCharacterStage friend={friend} previewImage={previewImage} name={name} compact />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {!paid ? (
          <motion.div
            key="sheet"
            variants={sheetVariants}
            className="fs-signin-sheet fc-wall-sheet"
            exit={{ y: '118%', transition: { duration: SHEET_DROP_S, ease: [0.5, 0, 0.75, 0.2] } }}
          >
            <AnimatePresence mode="wait" custom={dir} initial={false}>
              <motion.div
                key={step}
                className="fc-sheet-page"
                custom={dir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                {children}
              </motion.div>
            </AnimatePresence>
            <motion.p variants={{}} className="fs-signin-wordmark" aria-hidden="true">PIXLEY</motion.p>
          </motion.div>
        ) : (
          <motion.div
            key="welcome"
            className="fa-card fa-center fa-meet fc-success-card"
            variants={welcomeCardVariants}
            initial="enter"
            animate="center"
          >
            {success && React.cloneElement(success, { fromRect: fromRectRef.current, friend })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FunnelPaywallStep = ({
  childName = '',
  previewImage,
  plansData,
  billing,
  onSelectBilling,
  onCheckout,
  busy = false,
  error = '',
  // A promotion code in force (utils/funnelPromo.promoFromResponse), or null
  promo = null,
  // The plans request failed (vs. still loading: `plansData` null without it)
  loadError = false,
  onRetry,
  // The paywall offer experiment (hooks/useOfferVariant): the arm this
  // visitor is in, and in the pay-what-you-want arm the first month's price
  // they named (cents) with the handler that records a new one
  offerVariant = 'control',
  // The "finish setting up Pixley" email's invitation (utils/funnelOffer):
  // the sheet says why the first month's price is theirs to name
  invitation = false,
  chosenCents = null,
  onChooseCents,
  onPriceChooserOpened,
  itemVariants,
}) => {
  const name = childName.trim();
  const whose = name ? possessive(name) : 'their';
  // The named price only counts while the server offers it (validChosenCents)
  const named = billing === 'monthly' ? validChosenCents(plansData, offerVariant, chosenCents) : null;
  const experiment = { variant: offerVariant, chosenCents: named };
  const { available, prices, hasIntroOffer, introOffer, trialConfig } = paywallTermsFrom(plansData, promo, experiment);
  const loading = !plansData && !loadError;
  // Pay what you want: the chooser's terms for the month, when this visitor
  // gets one (never over a promotion code: one discount per session)
  const choose = promo ? null : pwywChoiceFor(plansData, offerVariant, billing);
  const [chooserOpen, setChooserOpen] = useState(false);
  const openChooser = () => {
    onPriceChooserOpened?.();
    setChooserOpen(true);
  };
  const hasTrial = (trialConfig?.duration_days || 0) > 0;
  // Confetti: once as the paywall opens, and again on every Monthly / Annual
  // flip. `burst` counts the falls so each one is a fresh overlay.
  const reducedMotion = (() => {
    try {
      return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    } catch (_) {
      return false;
    }
  })();
  const [burst, setBurst] = useState(() => {
    try {
      return !reducedMotion && !sessionStorage.getItem('pixley_funnel_paywall_confetti') ? 1 : 0;
    } catch (_) {
      return 1;
    }
  });
  useEffect(() => {
    if (!burst) return undefined;
    try {
      sessionStorage.setItem('pixley_funnel_paywall_confetti', '1');
    } catch (_) {
      // not remembered across a reload; it just falls again
    }
    const id = setTimeout(() => setBurst(0), CONFETTI_MS);
    return () => clearTimeout(id);
  }, [burst]);
  const flipBilling = (period) => {
    if (!reducedMotion) setBurst((n) => n + 1);
    onSelectBilling(period);
  };
  const confirmPrice = (cents) => {
    setChooserOpen(false);
    onChooseCents?.(cents);
    // a price of their own is worth a little celebration
    if (!reducedMotion && cents !== named) setBurst((n) => n + 1);
  };
  const priceAction = choose ? {
    label: Number.isInteger(named) ? 'Change your price' : 'Choose your price',
    hint: Number.isInteger(named) ? 'Your price \u00b7 tap to change' : 'Tap to set your price',
    chosen: Number.isInteger(named),
    onClick: openChooser,
  } : null;

  return (
    <div className="fc-sheet-content" data-testid="funnel-paywall">
      {burst > 0 && <PaywallEntryConfetti key={burst} />}
        <HandTitle
          lead={name ? `${whose} character` : 'Their character'}
          hero="is waiting"
          itemVariants={itemVariants}
        />
        <motion.p variants={itemVariants} className="fs-sub">
          {choose && invitation
            // From the email: the invitation, in its own words
            ? `We'd love to have you. So for ${whose} first month, pay whatever you think Pixley is worth. Cancel anytime.`
            : choose
              // Pay what you want: said plainly in the sheet's own sub line;
              // the dashed price field on the card carries the weight
              ? 'Pay what you want for your first month. Cancel anytime.'
              : 'Pick a plan to start making it. Cancel anytime.'}
        </motion.p>

        <motion.div variants={itemVariants} className="fc-wall-plans">
          {loading && (
            <div className="fc-plans-loading" role="status" aria-live="polite" aria-busy="true">
              <span className="fc-plans-spinner" aria-hidden="true" />
              Loading plans…
            </div>
          )}
          {!loading && !available && (
            <div className="fc-plans-unavailable" role="alert">
              <p>{PRICING_UNAVAILABLE}</p>
              {onRetry && (
                <button type="button" className="fc-retry" onClick={onRetry}>Try again</button>
              )}
            </div>
          )}
          {!loading && available && promo && (
            <p className="fc-promo" role="status">
              <span className="fc-saved-check" aria-hidden="true"><CheckIcon size={11} /></span>
              Code <strong>{promo.code}</strong> applied{promo.discountDisplay ? ` · ${promo.discountDisplay}` : ''}
            </p>
          )}
          {!loading && available && (
          <>
          <BillingToggle billingPeriod={billing} onChange={flipBilling} compact prices={prices} />
          <PlanGrid
            prices={prices}
            billingPeriod={billing}
            hasIntroOffer={hasIntroOffer}
            introOffer={introOffer}
            trialConfig={trialConfig}
            onSelect={onCheckout}
            isProcessing={busy}
            dense
            priceAction={priceAction}
          />
          </>
          )}
          {choose && (
            <FunnelPriceChooser
              open={chooserOpen}
              choose={choose}
              valueCents={Number.isInteger(named) ? named : choose.suggested_cents}
              childName={name}
              onConfirm={confirmPrice}
              onClose={() => setChooserOpen(false)}
            />
          )}
          {error && <p className="fc-error" role="alert">{error}</p>}
          {/* Walking away loses nothing — parents assume it does */}
          {previewImage && (
            <p className="fc-saved">
              <span className="fc-saved-check" aria-hidden="true"><CheckIcon size={11} /></span>
              {name ? `${whose} character is saved to your account.` : 'Their character is saved to your account.'}
            </p>
          )}
          <PaywallFootnote
            hasIntroOffer={hasIntroOffer && !(choose && Number.isInteger(named) && named >= choose.max_cents)}
            hasTrial={hasTrial}
            trialConfig={trialConfig}
            billingPeriod={billing}
          />
          <p className="fc-trust">
            <LockIcon /> Secure checkout by Stripe
          </p>
          <div className="fc-proof">
            <ReviewCard />
          </div>
        </motion.div>
    </div>
  );
};

// ── stripe.js ──────────────────────────────────────────────────────────────
// Loaded from Stripe's own origin only, once per page. Embedded Checkout
// renders in Stripe's iframe; this page never sees a card number.

const STRIPE_JS = 'https://js.stripe.com/v3/';
// A load that hangs (captive portal, a slow in-app browser) gives up after
// this long, so the sheet's fallback (onUnavailable) runs instead of a
// skeleton that never ends.
export const STRIPE_JS_TIMEOUT_MS = 15000;
let stripeJsPromise = null;

export const loadStripeJs = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (stripeJsPromise) return stripeJsPromise;
  stripeJsPromise = new Promise((resolve, reject) => {
    // A tag left by an earlier failed try is spent: its load/error events
    // already fired, so waiting on it would hang forever. Start over with a
    // fresh one.
    document.querySelectorAll(`script[src="${STRIPE_JS}"]`).forEach((old) => old.remove());
    const script = document.createElement('script');
    let timer = null;
    const fail = (message) => {
      clearTimeout(timer);
      script.remove();
      stripeJsPromise = null; // a later try loads it again
      reject(new Error(message));
    };
    timer = setTimeout(() => fail('Stripe.js timed out'), STRIPE_JS_TIMEOUT_MS);
    script.addEventListener('load', () => {
      clearTimeout(timer);
      if (window.Stripe) resolve(window.Stripe);
      else fail('Stripe.js unavailable');
    }, { once: true });
    script.addEventListener('error', () => fail('Stripe.js failed to load'), { once: true });
    script.src = STRIPE_JS;
    script.async = true;
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);
  });
  return stripeJsPromise;
};

// ── checkout ───────────────────────────────────────────────────────────────

/**
 * Stripe Embedded Checkout inside the funnel sheet. `clientSecret` and
 * `publishableKey` come from the backend; `onComplete` fires when Stripe
 * reports the payment done (the session was created with
 * redirect_on_completion=never, so there is no page reload). `onUnavailable`
 * is the escape hatch when Stripe.js can't load or mount: the page then falls
 * back to Stripe's hosted page.
 */
export const FunnelCheckoutStep = ({
  childName = '',
  offer,
  clientSecret,
  publishableKey,
  onComplete,
  onUnavailable,
  onChangePlan,
  // A line above the order card (a promotion code that could not be applied)
  notice = '',
  itemVariants,
}) => {
  const mountRef = useRef(null);
  const [state, setState] = useState('loading'); // loading | ready | failed
  const name = childName.trim();
  const whose = name ? possessive(name) : 'their';
  // The page re-renders while the form is up (polls, analytics); the latest
  // handlers are read through refs so the Stripe iframe is mounted exactly
  // once per session and never torn down by a re-render.
  const onCompleteRef = useRef(onComplete);
  const onUnavailableRef = useRef(onUnavailable);
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onUnavailableRef.current = onUnavailable;
  });

  useEffect(() => {
    if (!clientSecret || !publishableKey || !mountRef.current) return undefined;
    let cancelled = false;
    let checkout = null;
    (async () => {
      try {
        const Stripe = await loadStripeJs();
        if (cancelled) return;
        const stripe = Stripe(publishableKey);
        checkout = await stripe.initEmbeddedCheckout({
          clientSecret,
          onComplete: () => onCompleteRef.current?.(),
        });
        if (cancelled) {
          checkout.destroy();
          return;
        }
        checkout.mount(mountRef.current);
        setState('ready');
      } catch (error) {
        // Never log the secret; the error class is enough to diagnose
        logger.warn('Embedded Checkout unavailable:', error?.name || 'Error');
        if (!cancelled) {
          setState('failed');
          onUnavailableRef.current?.(error);
        }
      }
    })();
    return () => {
      cancelled = true;
      try {
        checkout?.destroy();
      } catch (_) {
        // already gone
      }
    };
  }, [clientSecret, publishableKey]);

  return (
    <div className="fc-sheet-content fc-checkout">
        <HandTitle hero="Almost there!" itemVariants={itemVariants} />
        {notice && <motion.p variants={itemVariants} className="fc-notice" role="status">{notice}</motion.p>}

        {/* The order: the character, the plan, what's charged today and what
            it renews at — in the plan cards' own style, so the checkout reads
            as the next page of the same thing */}
        <motion.div variants={itemVariants} className="fc-order">
          <div className="fc-order-top">
            <span className="fc-order-text">
              <span className="fc-order-plan">
                <strong>{offer?.name || 'Pro'}</strong>
                {offer?.chip && <em className="fc-order-chip">{offer.chip}</em>}
              </span>
              <small>{name ? `${whose} ${offer?.name || 'Pro'} plan` : `Pixley ${offer?.name || 'Pro'}`}</small>
            </span>
            <button type="button" className="fc-order-change" onClick={onChangePlan}>Change</button>
          </div>
          <div className="fc-order-price">
            <span className="fc-order-today">
              <span className="fc-order-amount">{offer?.headline}</span>
              <span className="fc-order-when">{offer?.kind === 'trial' ? offer.headlineNote : 'today'}</span>
            </span>
            <span className="fc-order-renew">
              {offer?.kind === 'plain' ? `billed ${offer.billing === 'annual' ? 'yearly' : 'monthly'}` : offer?.renewal} · cancel anytime
            </span>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="fs-eyebrow fc-checkout-eyebrow">
          <LockIcon /> Secure checkout
        </motion.div>

        <div className={`fc-stripe${state === 'ready' ? ' is-ready' : ''}`}>
          {state === 'loading' && (
            <div className="fc-stripe-skeleton" aria-label="Loading secure checkout" role="status">
              <span /><span /><span className="is-short" /><span className="is-button" />
            </div>
          )}
          {state === 'failed' && (
            <p className="fc-error" role="alert">
              The card form couldn't load here. Taking you to Stripe's secure page…
            </p>
          )}
          <div ref={mountRef} className="fc-stripe-mount" />
        </div>

    </div>
  );
};

// ── success ────────────────────────────────────────────────────────────────


/**
 * After paying: the character comes to life in the same frame it was revealed
 * in, while the membership activates behind the scenes. `activation` is
 * 'pending' until RevenueCat reflects the subscription, then 'active'; 'slow'
 * when the page stopped waiting (the home screen keeps polling).
 */
export const FunnelSuccessStep = ({
  childName = '',
  friend,
  previewImage,
  offer,
  activation = 'pending',
  onContinue,
  // The App Store button was tapped (the link itself opens the store)
  onDownloadApp,
  itemVariants,
  // 'morph': arrived from checkout on the shared stage — the frame starts
  // where the character stood (`fromRect`, measured by FunnelPayStage) and
  // drops into place while the ring and confetti follow. 'pop': the frame
  // pops in on its own (the hosted-Stripe return, a reload).
  entrance = 'pop',
  fromRect = null,
}) => {
  const name = childName.trim();
  const whose = name ? possessive(name) : 'their';
  const morph = entrance === 'morph';
  const heroRef = useRef(null);
  const heroControls = useAnimationControls();
  const [dropping, setDropping] = useState(morph && !!fromRect);
  // The flying copy: a ready-made friend's cut-out, the very picture that
  // stood on the stage, so the hand-off is invisible. A photo character
  // already stands in a frame on the stage, so its frame simply flies.
  const flySrc = morph && fromRect && friend?.key ? `/assets/funnel-friends/${friend.key}-cutout.webp` : null;
  useLayoutEffect(() => {
    if (!morph || !fromRect || !heroRef.current) return;
    const to = heroRef.current.getBoundingClientRect();
    if (!to.width || !to.height) {
      setDropping(false);
      return;
    }
    // Where the character stands RIGHT NOW, in the same layout pass and the
    // same (viewport) coordinates as the frame: the stage is still on screen
    // at this moment, and the page may just have lost its scroll (the sheet
    // leaving makes it shorter), which a box measured earlier wouldn't know.
    const stageEl = document.querySelector(
      '.fc-stage-wrap .fs-signin-friend, .fc-stage-wrap .fs-signin-frame, .fc-stage-wrap .fs-signin-mascot'
    );
    const live = stageEl?.getBoundingClientRect();
    const from = live && live.height
      ? live
      : { left: fromRect.left - window.scrollX, top: fromRect.top - window.scrollY, width: fromRect.width, height: fromRect.height };
    // The box that must coincide with the stage character is the flying
    // cut-out's (a friend) or the whole frame (a photo character, framed on
    // the stage too). The frame scales about its centre, so the delta is exact.
    const flyH = flySrc ? to.height - FRAME_PAD * 2 : to.height;
    const scale = from.height / flyH;
    const dx = from.left + from.width / 2 - (to.left + to.width / 2);
    const dy = from.top + from.height / 2 - (to.top + to.height / 2);
    if (process.env.NODE_ENV !== 'production') {
      window.__pixleyDrop = { from, to, dx, dy, scale, scrollY: window.scrollY };
    }
    const noChrome = {
      backgroundColor: 'rgba(255,255,255,0)',
      boxShadow: '0 14px 40px rgba(62,107,255,0), 0 0 0 1px rgba(22,38,66,0)',
    };
    heroControls.set({ x: dx, y: dy, scale, opacity: 0, ...noChrome });
    (async () => {
      // invisible until the stage character hands over
      await heroControls.start({ opacity: 1, transition: { delay: CHARACTER_LEAVE_S, duration: 0.02 } });
      await heroControls.start({
        x: 0, y: 0, scale: 1,
        backgroundColor: 'rgba(255,255,255,1)',
        boxShadow: '0 14px 40px rgba(62,107,255,0.20), 0 0 0 1px rgba(22,38,66,0.04)',
        transition: {
          duration: CHARACTER_MORPH_S, ease: [0.22, 1, 0.36, 1],
          // the frame's border arrives as it lands
          backgroundColor: { delay: CHARACTER_MORPH_S * 0.55, duration: 0.35 },
          boxShadow: { delay: CHARACTER_MORPH_S * 0.55, duration: 0.35 },
        },
      });
      setDropping(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setRevealed(true), morph ? (CHARACTER_LEAVE_S + CHARACTER_MORPH_S) * 1000 + 100 : 140);
    return () => clearTimeout(id);
  }, [morph]);
  // Confetti across the whole screen: on arrival, and again when the
  // membership flips to active
  const reducedMotion = (() => {
    try {
      return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    } catch (_) {
      return false;
    }
  })();
  const [burst, setBurst] = useState(reducedMotion || morph ? 0 : 1);
  useEffect(() => {
    // After the morph: the confetti comes once the sheet is gone and the
    // character has landed in its frame
    if (!morph || reducedMotion) return undefined;
    const id = setTimeout(() => setBurst((n) => n + 1), SUCCESS_CONFETTI_DELAY_MS);
    return () => clearTimeout(id);
  }, [morph, reducedMotion]);
  useEffect(() => {
    if (activation === 'active' && !reducedMotion) setBurst((n) => n + 1);
  }, [activation, reducedMotion]);
  useEffect(() => {
    if (!burst) return undefined;
    const id = setTimeout(() => setBurst(0), CONFETTI_MS);
    return () => clearTimeout(id);
  }, [burst]);

  const status = {
    pending: { text: `Activating ${whose} membership…`, tone: 'pending' },
    active: { text: 'Membership active', tone: 'active' },
    slow: { text: 'Almost there — finishing up in the app', tone: 'pending' },
  }[activation] || { text: '', tone: 'pending' };

  return (
    <div className="fc-success">
      {burst > 0 && <PaywallEntryConfetti key={burst} />}

      <motion.div variants={itemVariants} className="fs-eyebrow">Welcome to Pixley</motion.div>
      {name
        ? <HandTitle hero={name} tail="is in!" itemVariants={itemVariants} />
        : <HandTitle lead="You're in!" itemVariants={itemVariants} />}

      <motion.div
        ref={heroRef}
        animate={morph ? heroControls : undefined}
        className={`fs-hero fc-success-hero${revealed ? ' revealed' : ''}${morph ? ' is-morph' : ''}${dropping ? ' is-dropping' : ''}`}
        style={{ transformOrigin: '50% 50%' }}
      >
        <span className="fs-hero-ring" aria-hidden="true" />
        {flySrc ? (
          <img src={flySrc} alt={friend?.name || name || 'Their character'} className="fc-hero-fly" />
        ) : (
          <img
            src={previewImage || '/assets/pixley-panda-nobg.png'}
            alt={friend?.name || name || 'Their character'}
            className="fs-hero-img"
          />
        )}
      </motion.div>

      <motion.p variants={itemVariants} className="fs-sub">
        {name
          ? `${whose} first story is ready to make. Every week brings a new one.`
          : 'Your first story is ready to make. Every week brings a new one.'}
      </motion.p>

      <motion.div variants={itemVariants} className={`fc-activation is-${status.tone}`} role="status" aria-live="polite">
        {status.tone === 'active' ? <span className="fc-activation-check"><CheckIcon size={13} /></span> : <span className="fs-spinner" />}
        <span>{status.text}</span>
      </motion.div>

      <motion.div variants={itemVariants}>
        {/* The app is the point of the funnel: the store first, the web as the
            way to carry on right here */}
        <a
          className="fc-app-badge"
          href={appStoreUrl(googleReferred() ? 'web2app-google' : 'web2app')}
          target="_blank"
          rel="noreferrer"
          onClick={() => onDownloadApp?.()}
        >
          <img src={APP_STORE_BADGE} alt="Download on the App Store" />
        </a>
        <button type="button" className="fc-cta-secondary" onClick={onContinue}>
          Or make {whose} first story on the web <Chevron />
        </button>
        {offer?.kind === 'trial' && offer.trialDays > 0 && (
          <p className="fc-first-line">We'll remind you before your {offer.trialDays}-day trial ends.</p>
        )}
      </motion.div>
    </div>
  );
};

// ── styles ─────────────────────────────────────────────────────────────────
// Same palette as FunnelScreens: ink #162642, accent #296FFB→#3E8DFD, muted
// #6B7892, hairlines #E4E9F5, gold #FEC23C.

export const FunnelCheckoutStyles = () => (
  <style>{`
    /* ── the website's paywall inside the sign-in sheet ── */
    /* The character stands taller than sign-in's — larger than the compact
       stage, so it rises up into the sky above the sheet's arc */
    .fc-paywall .fs-signin-stage { margin-top: 10px; }
    .fc-paywall .fs-signin-friend { height: calc(var(--stage-h) * 1.12); margin-bottom: -10px; }
    /* a cut-out character made from a photo is usually a slim, upright
       figure, so it stands a good deal taller than a round friend here and
       still sits inside the moon */
    .fc-paywall .fs-signin-made {
      /* tall, but never above the step header or off a short screen: the
         stage container is --stage-h high and the figure may rise 35% above
         it, capped at 38vh; width-capped too, so a wide character shrinks
         to fit (object-fit: contain keeps the whole figure visible) */
      height: min(calc(var(--stage-h) * 1.35), 38vh);
      max-width: min(78vw, 320px);
      margin-bottom: -10px;
    }
    .fc-paywall .fs-signin-frame { width: calc(var(--stage-h) * 0.8); margin-bottom: 20px; }
    .fc-paywall .fs-signin-mascot { height: calc(var(--stage-h) * 0.95); }
    /* The sheet keeps sign-in's arc, shadow and type; the plan grid gets the
       site's room (two cards side by side above phone width) and a wash of
       the site's light-blue behind the white cards. */
    .fc-wall-sheet { padding-top: 26px; }
    .fc-wall-sheet > * { max-width: 640px; }
    .fc-sheet-page { width: 100%; display: flex; flex-direction: column; align-items: center; }
    .fc-sheet-page > * { width: 100%; max-width: 640px; }
    .fc-sheet-content { width: 100%; display: flex; flex-direction: column; align-items: center; }
    .fc-sheet-content > * { width: 100%; max-width: 640px; }
    .fc-wall-sheet .fs-eyebrow { margin-bottom: 8px; }
    .fc-wall-sheet .fs-hand-title { margin-bottom: 8px; }
    .fc-wall-sheet .fs-hand-lead { font-size: 34px; }
    .fc-wall-sheet .fs-hand-name { font-size: 42px; }
    .fc-wall-sheet .fs-sub { margin-bottom: 14px; font-size: 15.5px; }
    .fc-wall-plans {
      width: 100%;
      padding: 14px 18px 20px;
      font-family: 'Inter', 'Fredoka', sans-serif;
      text-align: center;
    }
    .fc-wall-plans .fc-error { margin-top: 14px; }
    .fc-wall-plans .fc-trust { margin-top: 10px; }
    .fc-plans-loading {
      display: flex; align-items: center; justify-content: center; gap: 10px;
      padding: 36px 0;
      font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; color: #6B7892;
    }
    .fc-plans-spinner {
      width: 18px; height: 18px; border-radius: 50%;
      border: 3px solid #D7E3F3; border-top-color: #0065F8;
      animation: fcSpin 0.8s linear infinite;
    }
    @keyframes fcSpin { to { transform: rotate(360deg); } }
    .fc-plans-unavailable {
      padding: 22px 16px;
      border-radius: 18px;
      background: #FFF4F6;
      font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.45; color: #B4233F;
    }
    .fc-plans-unavailable p { margin: 0 0 12px; }
    .fc-retry {
      padding: 10px 18px; border-radius: 12px; border: none; cursor: pointer;
      background: #0065F8; color: #FFF;
      font-family: 'Fredoka', 'Inter', sans-serif; font-size: 14px; font-weight: 700;
    }
    .fc-notice {
      margin: -4px 0 12px;
      padding: 10px 12px;
      border-radius: 14px;
      background: #FFF7E6; color: #7A4B00;
      font-family: 'Inter', sans-serif;
      font-size: 13px; line-height: 1.4; text-align: center;
    }
    .fc-promo {
      display: flex; align-items: center; justify-content: center; gap: 7px;
      margin: 0 0 12px;
      font-family: 'Inter', sans-serif;
      font-size: 13px; font-weight: 600;
      color: #1FA84A;
    }
    .fc-promo strong { font-weight: 800; letter-spacing: 0.04em; }
    .fc-saved {
      display: flex; align-items: center; justify-content: center; gap: 7px;
      margin: 14px 0 0;
      font-family: 'Inter', sans-serif;
      font-size: 13px; font-weight: 600;
      color: #334155;
    }
    .fc-saved-check {
      width: 18px; height: 18px; border-radius: 50%; flex: 0 0 auto;
      display: inline-flex; align-items: center; justify-content: center;
      background: #DDF6E4; color: #1FA84A;
    }
    /* the review (FunnelPlans.ReviewCard), centred under the fine print */
    .fc-proof {
      display: flex; justify-content: center;
      margin-top: 16px;
    }
    .fc-proof > * { min-width: 0; width: min(100%, 340px); }
    /* entry confetti: over the whole screen, falls once */
    .fc-confetti {
      position: fixed; inset: 0;
      z-index: 40;
      pointer-events: none;
      overflow: hidden;
    }
    .fc-confetti span {
      position: absolute;
      top: -24px;
      border-radius: 2px;
      opacity: 0.95;
      animation-name: fcConfettiFall;
      animation-timing-function: cubic-bezier(.3,.1,.6,1);
      animation-fill-mode: forwards;
    }
    @keyframes fcConfettiFall {
      0% { transform: translate(0, 0) rotate(0deg); opacity: 0.95; }
      85% { opacity: 0.95; }
      100% { transform: translate(var(--drift), 110vh) rotate(var(--spin)); opacity: 0; }
    }
    @media (max-width: 560px) {
      .fc-proof { gap: 8px; }
      /* Half a phone each: smaller type and badges so both read as one row */

    }

    @media (max-width: 480px) {
      /* One phone screen: the stage, the words and the whole Pro card */
      .fc-wall-sheet { padding-top: 24px; }
      .fc-wall-sheet .fs-eyebrow { margin-bottom: 3px; }
      .fc-wall-sheet .fs-hand-title { margin-bottom: 3px; }
      /* the title flows on one line ("Mia's character is waiting") */
      .fc-wall-sheet .fs-hand-lead { display: inline; font-size: 27px; }
      .fc-wall-sheet .fs-hand-name { font-size: 31px; padding: 0 3px; }
      .fc-wall-sheet .fs-sub { font-size: 14px; margin-bottom: 8px; }
      .fc-wall-plans { padding: 6px 10px 12px; }
    }

    .fc-sheet { padding-top: 30px; }
    .fc-sheet > * { max-width: 460px; }
    .fc-sheet .fs-hand-lead { font-size: 36px; }
    .fc-sheet .fs-hand-name { font-size: 50px; }

    /* fine print shared with the checkout step */
    .fc-first-line {
      margin: 12px 0 0;
      font-family: 'Inter', sans-serif;
      font-size: 13.5px; line-height: 1.45;
      color: #6B7892;
      text-wrap: balance;
    }
    .fc-trust {
      display: flex; align-items: center; justify-content: center; gap: 6px;
      margin: 12px 0 0;
      font-family: 'Inter', sans-serif;
      font-size: 12.5px; font-weight: 600;
      color: #7F8BA5;
    }
    .fc-trust svg { color: #1FA84A; }
    .fc-error {
      margin: 10px 0 0;
      padding: 10px 12px;
      border-radius: 14px;
      background: #FFF0F3; color: #B4233F;
      font-family: 'Inter', sans-serif;
      font-size: 13.5px; line-height: 1.4;
    }

    /* checkout */
    .fc-checkout .fs-hand-title { text-align: center; }
    .fc-checkout .fs-hand-name { font-size: 42px; }
    .fc-checkout .fs-hand-title { margin-bottom: 14px; }
    .fc-order {
      width: 100%;
      padding: 14px 14px 12px;
      border-radius: 22px;
      background: linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%);
      border: 2px solid rgba(0,101,248,0.14);
      box-shadow: 0px 3px 0px rgba(0,101,248,0.10);
      text-align: left;
      margin-bottom: 18px;
      font-family: 'Inter', 'Fredoka', sans-serif;
    }
    .fc-order-top { display: flex; align-items: center; gap: 12px; }
    .fc-order-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .fc-order-plan { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; row-gap: 4px; }
    .fc-order-plan strong {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 20px; font-weight: 700; text-transform: uppercase;
      color: #0B1A33; line-height: 1.1;
    }
    .fc-order-chip {
      white-space: nowrap;
      font-style: normal;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 11.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
      padding: 3px 9px; border-radius: 999px;
      background: #E8F2FF; color: #0065F8;
    }
    .fc-order-text small { font-size: 13px; color: #5A6B85; font-weight: 600; }
    .fc-order-change {
      flex-shrink: 0;
      padding: 8px 13px;
      border-radius: 12px;
      background: #FFF;
      border: 2px solid rgba(0,101,248,0.22);
      box-shadow: 0px 3px 0px rgba(11,26,51,0.06);
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 14px; font-weight: 700;
      color: #0065F8;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      transition: transform 100ms ease, box-shadow 100ms ease;
    }
    .fc-order-change:active { transform: translateY(3px); box-shadow: none; }
    /* today's charge on one line, the renewal on its own line under it */
    .fc-order-price {
      display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
      margin-top: 12px; padding-top: 12px;
      border-top: 1.5px dashed rgba(0,101,248,0.18);
    }
    .fc-order-today { display: flex; align-items: baseline; gap: 6px; }
    .fc-order-amount {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 28px; font-weight: 700; line-height: 1;
      letter-spacing: -0.02em;
      color: #0B1A33;
      font-variant-numeric: tabular-nums;
    }
    .fc-order-when { font-size: 13px; font-weight: 700; color: #334155; }
    .fc-order-renew { font-size: 12.5px; color: #5A6B85; text-align: left; }
    @media (max-width: 400px) {
      .fc-order { padding: 12px 12px 10px; }
      .fc-order-top { gap: 10px; }
      .fc-order-plan strong { font-size: 18px; }
      .fc-order-change { padding: 7px 11px; font-size: 13px; }
      .fc-order-amount { font-size: 26px; }
      .fc-order-renew { font-size: 11.5px; }
    }
    .fc-checkout-eyebrow { display: flex; align-items: center; justify-content: center; gap: 6px; margin-bottom: 10px; }
    .fc-stripe { width: 100%; position: relative; min-height: 360px; }
    .fc-stripe-mount { width: 100%; }
    .fc-stripe-skeleton {
      position: absolute; inset: 0;
      display: flex; flex-direction: column; gap: 14px;
      padding: 6px 2px;
    }
    .fc-stripe-skeleton span {
      display: block; height: 46px; border-radius: 12px;
      background: linear-gradient(90deg, #EEF2FA 0%, #F8FAFE 50%, #EEF2FA 100%);
      background-size: 200% 100%;
      animation: fcShimmer 1.3s linear infinite;
    }
    .fc-stripe-skeleton .is-short { width: 55%; }
    .fc-stripe-skeleton .is-button { height: 52px; margin-top: 10px; background: linear-gradient(90deg, #DCE7FF 0%, #EAF1FF 50%, #DCE7FF 100%); background-size: 200% 100%; }
    @keyframes fcShimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

    /* success */
    .fc-success { position: relative; }
    /* Apple's badge, as-is: never restyled, min 40px tall, clear space around */
    .fc-app-badge { display: inline-block; margin: 6px 0 2px; line-height: 0; }
    .fc-app-badge img { height: 56px; width: auto; display: block; }
    .fc-app-badge:focus-visible { outline: 3px solid #3E8DFD; outline-offset: 4px; border-radius: 10px; }
    .fc-cta-secondary {
      display: inline-flex; align-items: center; justify-content: center; gap: 6px;
      margin-top: 12px; padding: 10px 14px;
      border: none; background: none; cursor: pointer;
      font-family: 'Inter', sans-serif; font-size: 14.5px; font-weight: 700;
      color: #296FBB;
    }
    .fc-cta-secondary svg { width: 14px; height: 14px; }
    /* The welcome card on the shared stage: the page's white card, centred,
       at the width the rest of the funnel's cards use */
    .fc-success-card { width: 100%; max-width: 520px; margin: 0 auto; }
    /* Once paid, the leaving sheet and stage come out of the flow at once:
       the welcome card takes its final place immediately, so the frame
       measures the right landing spot and the card never jumps */
    .fc-paywall.is-paid { justify-content: center; position: relative; }
    .fc-paywall.is-paid .fs-signin-sheet { position: absolute; left: 0; right: 0; bottom: 0; z-index: 1; pointer-events: none; }
    .fc-paywall.is-paid .fc-stage-wrap { position: absolute; left: 0; right: 0; top: 0; z-index: 2; pointer-events: none; }
    .fc-paywall.is-paid .fc-success-card { position: relative; z-index: 3; }
    /* The frame framer morphs into: no CSS pop (its transform would fight the
       layout animation); it arrives visible and the ring fills once landed */
    /* measured at its real size for the drop; framer's inline transform then
       carries it from the stage into place */
    .fc-success-hero.is-morph { animation: none; opacity: 1; transform: none; }
    /* The welcome's ring is Pixley blue, filling clockwise once the frame has
       landed (no spin), and a gold star lands on the corner after it */
    .fc-success-hero .fs-hero-ring::before {
      background: conic-gradient(from 0deg, #296FFB, #3E8DFD, #5AA3FF, #3E8DFD, #296FFB);
      animation: none;
    }
    .fc-success-hero.revealed .fs-hero-ring::before { animation: none; }
    }
    /* while it's in flight the ring waits, and the frame floats above the card's words */
    .fc-success-hero.is-dropping { z-index: 3; }
    .fc-success-hero.is-dropping .fs-hero-ring { display: none; }
    /* a friend's cut-out is the frame's picture: it flies in and stays, centred */
    .fc-hero-fly {
      position: absolute; top: 7px; left: 7px;
      width: calc(100% - 14px); height: calc(100% - 14px);
      object-fit: contain; object-position: 50% 50%;
      z-index: 2; pointer-events: none;
    }
    .fc-success-hero { width: min(100%, 230px); margin-bottom: 18px; }
    .fc-success .fs-hand-lead { font-size: 44px; }
    .fc-success .fs-hand-name, .fc-success .fs-hand-tail { font-size: 46px; }
    .fc-activation {
      display: inline-flex; align-items: center; gap: 9px;
      margin: 0 auto 18px;
      padding: 8px 14px;
      border-radius: 999px;
      font-family: 'Inter', sans-serif;
      font-size: 14px; font-weight: 600;
      background: #F2F6FD; color: #3E4C66;
      transition: background 0.3s, color 0.3s;
    }
    .fc-activation .fs-spinner { width: 16px; height: 16px; border-width: 2.5px; }
    .fc-activation.is-active { background: #DDF6E4; color: #167A3A; }
    .fc-activation-check {
      width: 18px; height: 18px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      background: #1FA84A; color: #FFF;
      animation: fsPop 0.4s cubic-bezier(.34,1.5,.5,1);
    }

    @media (max-width: 480px) {
      .fc-sheet { padding-left: 20px; padding-right: 20px; }
      .fc-sheet .fs-hand-lead { font-size: 33px; }
      .fc-sheet .fs-hand-name { font-size: 46px; }
      .fc-plan { padding: 16px 12px 12px; border-radius: 20px; }
      .fc-plan-amount { font-size: 27px; }
      .fc-perk-label { font-size: 12px; }
    }
    @media (max-width: 374px) {
      .fc-plans { grid-template-columns: 1fr; }
      .fc-perks { gap: 6px; }
      .fc-perk-icon { width: 36px; height: 36px; }
    }
    @media (max-height: 700px) {
      .fc-sheet { padding-top: 24px; }
      .fc-sub { margin-bottom: 14px; }
      .fc-perks { margin-bottom: 14px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fc-stripe-skeleton span { animation: none; }
      .fc-activation-check { animation: none; }
    }
  `}</style>
);
